import React, { useMemo, useCallback } from 'react';
import { Book } from 'lucide-react';
import BiographySection from './BiographySection';
import EditableContent from './EditableContent';
import { BiographyField } from './types';
import { CharacterData } from '../../types/character';

interface BackgroundSectionProps {
  character: CharacterData;
  updateCharacterData: (updates: any) => void;
  defaultExpanded?: boolean;
  priority?: 'high' | 'medium' | 'low';
}

/**
 * BackgroundSection - Specialized section for character background
 * Uses BiographySection + EditableContent composition
 */
const BackgroundSection: React.FC<BackgroundSectionProps> = ({
  character,
  updateCharacterData,
  defaultExpanded = true,
  priority = 'high'
}) => {
  // Convert character background data to biography fields
  const fields = useMemo((): BiographyField[] => {
    const backgroundAnswers = character.biography?.backgroundAnswers || [];

    return backgroundAnswers.map((answer, index) => ({
      id: `background-${index}`,
      label: answer.question || `Background Question ${index + 1}`,
      value: answer.answer || '',
      type: 'textarea' as const,
      editable: true,
      placeholder: 'Describe your character\'s background...',
      maxLength: 500
    }));
  }, [character.biography?.backgroundAnswers]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldId: string, value: string | string[]) => {
    const backgroundAnswers = [...(character.biography?.backgroundAnswers || [])];
    const answerIndex = parseInt(fieldId.replace('background-', ''));

    if (answerIndex >= 0 && answerIndex < backgroundAnswers.length) {
      backgroundAnswers[answerIndex] = {
        ...backgroundAnswers[answerIndex],
        answer: typeof value === 'string' ? value : value[0] || ''
      };

      const updates = {
        biography: {
          ...character.biography,
          backgroundAnswers
        }
      };
      updateCharacterData(updates);
    }
  }, [character.biography, updateCharacterData]);

  // Handle save (for any additional validation or processing)
  const handleSave = useCallback(async () => {
    // Additional save logic if needed
    console.log('Background data saved');
  }, []);

  // Never mark as empty - always show fields so users can edit/add
  const isEmpty = false;

  return (
    <BiographySection
      sectionId="bio-background"
      title="Character Background"
      icon={<Book size={18} />}
      defaultExpanded={defaultExpanded}
      priority={priority}
      isEmpty={isEmpty}
      emptyMessage="No background questions answered yet"
    >
      {fields.length === 0 ? (
        <div className="biography-empty">
          <p className="biography-empty__message">
            No background questions from character creation. You can add custom background information in other sections.
          </p>
        </div>
      ) : (
        <EditableContent
          fields={fields}
          onFieldChange={handleFieldChange}
          onSave={handleSave}
          maxFieldsPerRow={1}
          showLabels={true}
          useCard={false}
        />
      )}
    </BiographySection>
  );
};

export default BackgroundSection;